import { useState, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import set from 'lodash/set';

import CardHeader from 'components/CardHeader';
import Card from 'components/Card';
import ShowMoreButton from 'components/ShowMoreButton';
import Table from 'components/Table';
import Header from 'components/Table/Header';
import Cell from 'components/Cell';
import Pills from 'components/Pills';
import Pellet from 'components/Pellet';
import ExportButton from 'components/Export';
import ContentTooltip from 'components/ContentTooltip';
import { useNetworkLabels } from 'contexts/ProductTypeContext';

const PillPos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-transform: none !important;
  p {
    margin-left: 4px;
    white-space: nowrap;
  }
`;

const TravelTableCardContainer = styled.div`
  .card-header {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 20px;

    @media (max-width: 845px) {
      display: block;
      gap: 16px;
    }
  }

  .left {
    justify-content: left;
    padding-left: 10%;
  }

  .exportButtonContainer {
    padding-bottom: 10px;
    margin-top: 10px;
  }

  .ttTitle {
    text-align: center;
    white-space: normal;

    @media (max-width: 845px) {
      font-size: 16px;
    }
  }

  .columnTextOverflow {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-left: 12px;
    padding-right: 12px;
  }

  .ttTitle span {
    color: #e25c32;
    margin-right: 10px;
  }

  .ttTitle .posNumber {
    margin-right: 0;
    margin-left: 5px;
  }

  .showAllContainer {
    margin-left: auto;
    margin-right: auto;
    width: 200px;

    @media (max-width: 845px) {
      width: 100%;
    }
  }
`;

const sortByQuality = (a: any, b: any) => {
  const aMichelinStars = a.michelin_stars;
  const bMichelinStars = b.michelin_stars;
  const aWsAward = a.ws_award;
  const bWsAward = b.ws_award;

  // Check if michelin_stars has value (not null, undefined, empty string, or 0)
  const aHasMichelin =
    aMichelinStars !== null &&
    aMichelinStars !== undefined &&
    aMichelinStars !== '' &&
    aMichelinStars !== 0;
  const bHasMichelin =
    bMichelinStars !== null &&
    bMichelinStars !== undefined &&
    bMichelinStars !== '' &&
    bMichelinStars !== 0;

  // Check if ws_award has value (not null, undefined, or empty string)
  const aHasAward =
    aWsAward !== null && aWsAward !== undefined && aWsAward !== '';
  const bHasAward =
    bWsAward !== null && bWsAward !== undefined && bWsAward !== '';

  // Check if has any quality indicator at all
  const aHasAnyQuality = aHasMichelin || aHasAward;
  const bHasAnyQuality = bHasMichelin || bHasAward;

  // If both have no quality indicators, they're equal
  if (!aHasAnyQuality && !bHasAnyQuality) {
    return 0;
  }

  // Push entries with no quality to the end
  if (!aHasAnyQuality) return 1;
  if (!bHasAnyQuality) return -1;

  // Compare by michelin stars first (descending: higher stars come first)
  const michelinDiff =
    Number(bMichelinStars || 0) - Number(aMichelinStars || 0);
  if (michelinDiff !== 0) {
    return michelinDiff;
  }

  // If michelin stars are equal, sort by ws_award (with awards come before without)
  if (aHasAward && !bHasAward) {
    return -1;
  }
  if (!aHasAward && bHasAward) {
    return 1;
  }

  return 0;
};

type TravelTableCardProps = {
  name: string;
  title: string;
  data: any[];
  retractedSize: number;
  isBranded: boolean;
  hasWineshopAccess: boolean;
  exportName: string;
  exportOptions: {
    path: string;
    options: any;
  };
  addReferencesColumn?: boolean;
  children?: React.ReactNode;
};

const TravelTableCard = ({
  name,
  title,
  data = [],
  retractedSize,
  isBranded,
  hasWineshopAccess,
  exportName,
  exportOptions,
  addReferencesColumn,
  children
}: TravelTableCardProps) => {
  const [sorted, setSorted] = useState<any[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [activePill, setActivePill] = useState('all');

  const { getOnLabel, getOffLabel } = useNetworkLabels();

  const handleSort = (id: string) => {
    let newSorted = [...sorted];
    let desc = true;
    if (newSorted.length > 0 && newSorted[0].id === id) {
      desc = !newSorted[0].desc;
    }
    newSorted = [{ id, desc }];
    setSorted(newSorted);
  };

  const pills = useMemo(() => {
    const restaurantsLength = data.filter(
      (item) => item.network_code === 'ON'
    ).length;
    const cavesLength = data.filter(
      (item) => item.network_code === 'OFF'
    ).length;

    return [
      { label: <PillPos>{I18n.t('placeholderAll')}</PillPos>, value: 'all' },
      {
        label: (
          <PillPos>
            <Pellet />
            <p>{`${getOnLabel(
              I18n.t('travel.typeOptions.restaurant')
            )} (${restaurantsLength})`}</p>
          </PillPos>
        ),
        value: 'ON'
      },
      {
        label: (
          <PillPos>
            <Pellet square={true} />
            <p>
              {getOffLabel(I18n.t('travel.typeOptions.cave'))} ({cavesLength})
            </p>
          </PillPos>
        ),
        value: 'OFF'
      }
    ];
  }, [data, getOnLabel, getOffLabel]);

  const columns = useMemo(() => {
    const cols = [
      {
        accessor: 'type',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.type')}
            styles={`.title{font-size:12px}; justify-content: left; padding-left: 10%;`}
          />
        ),
        Cell: (props: any) => (
          <Cell className="left" type="pellet" {...props} />
        ),
        width: 120
      },
      {
        accessor: 'name',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.name')}
            sortable={true}
            sort={() => handleSort('name')}
            styles={`.title{font-size:12px}; justify-content: left; padding-left: 10%;`}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="topLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'address',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.address')}
            styles={`.title{font-size:12px}; justify-content: left; padding-left: 10%;`}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="topLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'city',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.city')}
            sortable={true}
            sort={() => handleSort('city')}
            styles={`.title{font-size:12px}; justify-content: left; padding-left: 10%;`}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            className="columnTextOverflow"
            value={
              <Tooltip placement="topLeft" title={props.value}>
                {props.value}
              </Tooltip>
            }
          />
        )
      },
      {
        accessor: 'quality',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.quality')}
            styles={`.title{font-size:12px}; justify-content: left; padding-left: 10%;`}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            type="quality"
            styles={`justify-content:flex-start;padding-left:30px`}
            hideEmpty={
              props.value.stars === 0 ||
              props.value.stars === '' ||
              props.value.stars === null
            }
          />
        ),
        sortMethod: sortByQuality
      },
      {
        accessor: 'contact',
        Header: () => (
          <Header title={I18n.t('restaurants&caves.pos.columns.contact')} />
        ),
        Cell: (props: any) => (
          <Cell type="contact" {...props} font="sfcompact_text" />
        ),
        width: 150
      }
    ];

    if (isBranded && addReferencesColumn) {
      const clientBrandsColumn = {
        accessor: 'client_wines_by_brand',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.brand')}
            sortable={true}
            sort={() => handleSort('client_wines_by_brand')}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            tooltip={
              props.value && Object.values(props.value).length > 0
                ? {
                    placement: 'top',
                    content: (
                      <ContentTooltip
                        values={Object.keys(props.value)}
                        title={I18n.t('tooltipBrand')}
                      />
                    )
                  }
                : undefined
            }
            value={props.value ? Object.values(props.value).length : 0}
            font="sfcompact_text"
          />
        ),
        sortMethod: (a: any, b: any) =>
          a && b
            ? Number(Object.values(a).length) - Number(Object.values(b).length)
            : 0,
        width: 100
      };

      const rivalsBrandsColumn = {
        accessor: 'rivals_wines_by_brand',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.rivals')}
            sortable={true}
            sort={() => handleSort('rivals_wines_by_brand')}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            tooltip={
              props.value
                ? {
                    placement: 'top',
                    content: (
                      <ContentTooltip
                        values={Object.keys(props.value)}
                        title={I18n.t('tooltipRivalBrand')}
                      />
                    )
                  }
                : undefined
            }
            value={props.value ? Object.values(props.value).length : 0}
            font="sfcompact_text"
          />
        ),
        sortMethod: (a: any, b: any) =>
          Number(a ? Object.values(a).length : 0) -
          Number(b ? Object.values(b).length : 0),
        width: 100
      };
      cols.splice(2, 0, clientBrandsColumn as any, rivalsBrandsColumn as any);
    } else if (addReferencesColumn) {
      const clientRefsColumn = {
        accessor: 'client_refs_by_wine',
        Header: () => (
          <Header
            title={I18n.t('travel.tableColumns.refs')}
            sortable={true}
            sort={() => handleSort('client_refs_by_wine')}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            value={
              props.value
                ? Object.values(props.value).reduce(
                    (acc: number, base: any) => acc + base,
                    0
                  )
                : '-'
            }
            tooltip={
              props.value
                ? {
                    placement: 'top',
                    content: (
                      <ContentTooltip
                        title={I18n.t('tooltipWine')}
                        values={Object.keys(props.value)}
                      />
                    )
                  }
                : null
            }
            styles={`border-right: 1px solid #e8e9eb`}
          />
        ),
        sortMethod: (a: any, b: any) =>
          Number(
            a
              ? Object.values(a).reduce(
                  (acc: number, base: any) => acc + base,
                  0
                )
              : 0
          ) -
          Number(
            b
              ? Object.values(b).reduce(
                  (acc: number, base: any) => acc + base,
                  0
                )
              : 0
          )
      };
      cols.splice(2, 0, clientRefsColumn);
    } else {
      const rivalsRefsColum = {
        accessor: 'rival_wines',
        Header: () => (
          <Header
            sortable={true}
            title={I18n.t('travel.tableColumns.rivals')}
            sort={() => handleSort('rival_wines')}
          />
        ),
        Cell: (props: any) => (
          <Cell
            {...props}
            value={props.value ? Object.values(props.value).length : '0'}
            tooltip={
              props.value
                ? {
                    placement: 'top',
                    content: (
                      <ContentTooltip
                        title={I18n.t('tooltipRivalWine')}
                        values={Object.keys(props.value)}
                      />
                    )
                  }
                : null
            }
            styles={`border-right: 1px solid #e8e9eb`}
          />
        ),
        sortMethod: (a: any, b: any) =>
          Number(a ? Object.values(a).length : 0) -
          Number(b ? Object.values(b).length : 0),
        width: 100
      };
      cols.splice(2, 0, rivalsRefsColum as any);
    }

    return cols.filter((item) => !!item);
  }, [isBranded, addReferencesColumn]);

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (activePill !== 'all') {
      filtered = filtered.filter(
        (item) => item.type.network_code === activePill
      );
    }
    return filtered;
  }, [data, activePill]);

  const subSetData = useMemo(() => {
    return showAll ? filteredData : filteredData.slice(0, retractedSize);
  }, [filteredData, showAll, retractedSize]);

  const renderButtonShowMore = () => {
    if (filteredData.length <= retractedSize) {
      return null;
    }

    if (showAll) {
      return (
        <ShowMoreButton
          label={`${I18n.t('travel.seeLess')} (${retractedSize})`}
          onClick={() => setShowAll(false)}
        />
      );
    }

    return (
      <ShowMoreButton
        label={`${I18n.t('travel.seeMoreTable')} (+${
          filteredData.length - retractedSize
        })`}
        onClick={() => setShowAll(true)}
      />
    );
  };

  const updatedExportOptions = useMemo(() => {
    const options = { ...exportOptions };
    set(options, 'options.network_name', activePill);
    return options;
  }, [exportOptions, activePill]);

  const renderHeader = () => (
    <CardHeader
      title={
        <div className="card-header">
          <h1 className="ttTitle">
            {title}
            <span className="posNumber">({data.length})</span>
          </h1>
          {children || (
            <div className="ttPillsContainer ignored-export">
              {hasWineshopAccess && (
                <Pills
                  options={pills.map((item) => ({
                    ...item,
                    active: activePill === item.value
                  }))}
                  onChange={({ value }: any) => setActivePill(value)}
                  styles={`justify-content: space-between;`}
                />
              )}
            </div>
          )}
        </div>
      }
      rightComponents={
        <ExportButton
          jpg
          pdf
          xls
          {...updatedExportOptions}
          name={exportName + title}
          targetElement={`#voyage-pdv-${title.replace(/ /gi, '-')}`}
          disabled={data.length === 0}
          newApi
        />
      }
      fontSize="20px"
      iconColor="#e25c32"
      iconRight
    />
  );

  return (
    <TravelTableCardContainer>
      <Card id={`travel-pdv-${name}`}>
        {renderHeader()}
        <Table
          data={subSetData}
          columns={columns}
          sorted={sorted}
          subRowsKey={null}
        />
        <div className="showAllContainer ignored-export">
          {renderButtonShowMore()}
        </div>
      </Card>
    </TravelTableCardContainer>
  );
};

export default TravelTableCard;
